import { linkIdentifier } from "../../../utils/linkIdentifier";
import SvgComponent from "../SVGComponent";
import TickerAnimation from "../TickerAnimation";

/**
 * @typedef {((e: React.MouseEvent<HTMLButtonElement, MouseEvent>?) => void)} OnClickEventHandler
 */
/**
 * @typedef {("white" | "black")} HoverOption
 * @param {Object} props
 * @param {boolean} props.isDisabled - Is the click disabled. This only adds cursor-not-allowed, the actual onClick is still called.
 * @param {String} props.title - Title to be shown
 * @param {String} props.titleColor - Color of the title. Specify the color as mentioned in tailwind.config.js. eg: text-primary, text-secondary, pending, etc.
 * @param {String} props.subtitle - Subtitle to be shown
 * @param {OnClickEventHandler} props.onClick - What to do on click
 * @param {boolean} props.info - If you want to display the info icon adjoining the title eg:- true or false
 * @param {boolean} props.showArrow - If you want to display the arrow icon adjoining the title eg:- true or false
 * @param {HoverOption} props.hoverOption - Hover Option "black" or "white"
 * @param {boolean} props.isBold - If you want bold or regular weight of title eg:- true or false
 * @param {("xl" | "lg" | "md" | "sm")} props.variant
 * @param {("right" | "left")} props.textAlign
 * @param {boolean} props.enableticker
 * @param {boolean} props.truncate - If true, title will be truncated with ellipsis instead of wrapping or ticker

 * @param {("title" | "sentence" | "lower" | "upper")} props.titleCase
 * @returns {JSX.Element}
 */
const TitleSubtitlePair = ({
  isDisabled = false,
  title = "",
  titleColor = "text-primary",
  subtitle = "",
  onClick,
  info = false,
  showArrow = false,
  hoverOption = "black",
  isBold = false,
  variant = "md",
  textAlign = "left",
  enableTicker = false,
  truncate = false,
  titleCase,
  fontSize,
  subtitleIcon,
}) => {
  const truncateClass = truncate ? "truncate" : "";
  let titleClass = "";
  let subTitleClass = "";

  switch (variant) {
    case "xl":
      titleClass = "text-font-xl";
      subTitleClass = "text-font-sm";
      break;
    case "lg":
      titleClass = "text-font-lg";
      subTitleClass = "text-font-sm";
      break;
    case "md":
      titleClass = "text-font-md";
      subTitleClass = "text-font-xs";
      break;
    case "sm":
      titleClass = "text-font-sm";
      subTitleClass = "text-font-xs";
      break;
    default:
      titleClass = "text-font-xs";
      subTitleClass = "text-font-xs";
  }

  const textStyle = {
    hyphens: "auto",
    WebkitHyphens: "auto",
    MozHyphens: "auto",
    msHyphens: "auto",
    overflowWrap: "anywhere",
    wordBreak: "normal",
    minWidth: 0,
  };

  let transformedTitle = title;
  if (titleCase) {
    switch (titleCase) {
      case "title":
        transformedTitle = title
          .split(" ")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
          .join(" ");
        break;
      case "sentence":
        transformedTitle =
          title.charAt(0).toUpperCase() + title.slice(1).toLowerCase();
        break;
      case "lower":
        transformedTitle = title.toLowerCase();
        break;
      case "upper":
        transformedTitle = title.toUpperCase();
        break;
    }
  }

  return (
    <div
      className={`flex flex-col gap-y-gap-xs min-w-0
        ${textAlign === "right" ? "items-end text-right" : "items-start"}
        ${showArrow && !isDisabled ? "relative p-padding-sm rounded-border-radius-sm" : ""}
        ${
          showArrow && !isDisabled && hoverOption === "black"
            ? "z-z-index-base global-hover-black"
            : ""
        }
        ${
          showArrow && !isDisabled && hoverOption === "white"
            ? "z-z-index-base global-hover-white"
            : ""
        }
        ${
          isDisabled && showArrow
            ? "relative p-padding-sm rounded-border-radius-sm cursor-not-allowed opacity-light-opacity"
            : !isDisabled && showArrow
              ? "cursor-pointer"
              : "cursor-auto"
        }
      `}
      onClick={!info ? onClick : undefined}
    >
      <div
        className={`flex flex-row ${truncate || enableTicker ? "w-width-full" : ""} min-w-0 ${
          info ? "gap-x-gap-xs" : showArrow ? "gap-x-gap-sm" : ""
        }`}
      >
        {enableTicker ? (
          <div
            className={`text-${titleColor} ${isBold ? "font-semibold" : "font-normal"} w-width-full min-w-0`}
          >
            <TickerAnimation
              text={linkIdentifier(transformedTitle)}
              fontSize={variant}
            />
          </div>
        ) : (
          <p
            lang="en"
            title={truncate ? title : undefined}
            className={`${titleClass} text-${titleColor} flex-1 min-w-0
      ${textAlign === "right" ? "text-right" : "text-left"}
      ${isBold ? "font-semibold" : "font-normal"}
      ${truncateClass}
    `}
            style={!truncate ? textStyle : undefined}
          >
            {linkIdentifier(transformedTitle)}
          </p>
        )}

        {info && (
          <div
            className="relative cursor-pointer flex-shrink-0"
            onClick={onClick}
          >
            <img
              src={"https://q2pay.in/new_assets/common/detail_page/info.svg"}
              className="w-icon-sm h-icon-sm"
              alt="info"
            />
          </div>
        )}

        {showArrow && (
          <div className="relative flex items-center flex-shrink-0">
            <SvgComponent
              url={
                "https://q2pay.in/new_assets/common/arrows/arrow_right_white.svg"
              }
              height={"var(--icon-sm)"}
              width={"var(--icon-sm)"}
              color={"var(--text-primary)"}
              alt="redirect"
            />
          </div>
        )}
      </div>
      <div className="flex gap-gap-xs items-center">
        <p className={`${subTitleClass} text-text-secondary`}>{subtitle}</p>
        {subtitleIcon && (
          <SvgComponent
            url={subtitleIcon}
            height="var(--icon-sm)"
            width="var(--icon-sm)"
          />
        )}
      </div>
    </div>
  );
};

export default TitleSubtitlePair;
